import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  Badge,
  Button,
  Card,
  Flex,
  Group,
  Loader,
  SegmentedControl,
  Stack,
  Text,
  Title,
} from "@mantine/core";

import { EmptyMessage, ManagementLayout } from "@components/shared";
import { PERMISSIONS } from "@util/permissions";
import useAppStore from "@duck/app-store";
import usePermission from "@util/usePermission";
import AssessmentViewerLinksPanel from "./AssessmentViewerLinksPanel";
import OrganizationAssignmentRequestCreate from "./OrganizationAssignmentRequestCreate";

function personLabel(person, fallbackId) {
  if (!person) return fallbackId ? String(fallbackId) : "—";
  const name = `${person.first_name || ""} ${person.last_name || ""}`.trim();
  if (name && person.email) return `${name} (${person.email})`;
  return name || person.email || (fallbackId ? String(fallbackId) : "—");
}

function instrumentLabel(instrument, fallbackId) {
  if (!instrument) return fallbackId ? String(fallbackId) : "—";
  const variety = instrument.variety_formatted
    ? ` (${instrument.variety_formatted})`
    : "";
  return `${instrument.name || fallbackId || "—"}${variety}`;
}

export default function OrganizationAssignmentRequests({ organizationId }) {
  const { t } = useTranslation();
  const currentUser = useAppStore((state) => state.user);
  const [scope, setScope] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const canSeePage = usePermission(
    [
      PERMISSIONS.ASSESSMENTS_APPROVE,
      PERMISSIONS.ASSESSMENTS_ORG_APPROVE,
      PERMISSIONS.ASSESSMENTS_REQUEST,
    ],
    { mode: "any" }
  );
  const canPlatformDecide = usePermission(PERMISSIONS.ASSESSMENTS_APPROVE);
  const canOrgDecide = usePermission(PERMISSIONS.ASSESSMENTS_ORG_APPROVE);
  const canRequest = usePermission(PERMISSIONS.ASSESSMENTS_REQUEST);

  useEffect(() => {
    if (!canSeePage || scope !== null) return;
    if (canOrgDecide) return setScope("waiting_org");
    if (canPlatformDecide) return setScope("waiting_platform");
    setScope("mine");
  }, [canSeePage, canOrgDecide, canPlatformDecide, scope]);

  useEffect(() => {
    if (!canSeePage || scope === null) return;
    fetchData();
  }, [organizationId, scope, canSeePage]);

  function fetchData() {
    setLoading(true);
    axios
      .post(
        `/organizations/${organizationId}/retrieve-assessment-assignment-requests/`,
        { page: 0, page_size: 100, scope }
      )
      .then(({ data }) => {
        setRequests(data.response[0]?.data || []);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  function decide(requestId, approve, usePlatform) {
    setBusyId(requestId);
    const url = usePlatform
      ? `/assessment-assignment-requests/${requestId}/decision/`
      : `/organizations/${organizationId}/assessment-assignment-requests/${requestId}/decision/`;

    axios
      .post(url, { approve })
      .then(() => {
        setBusyId(null);
        toast.success(t("updated"));
        fetchData();
      })
      .catch((err) => {
        setBusyId(null);
        toast.error(err);
      });
  }

  function withdraw(requestId) {
    setBusyId(requestId);
    axios
      .post(
        `/organizations/${organizationId}/assessment-assignment-requests/${requestId}/withdraw/`,
        {}
      )
      .then(() => {
        setBusyId(null);
        toast.success(t("updated"));
        fetchData();
      })
      .catch((err) => {
        setBusyId(null);
        toast.error(err);
      });
  }

  function canWithdraw(request) {
    const waitingOrg = request.workflow_status === 0;
    const waitingPlatform = request.workflow_status === 1;
    if (!waitingOrg && !waitingPlatform) return false;

    const isRequester =
      currentUser?.id &&
      String(request.requested_by_id) === String(currentUser.id);
    if (isRequester) return true;
    if (canOrgDecide && waitingOrg) return true;
    return false;
  }

  if (!canSeePage) {
    return (
      <EmptyMessage
        title={t("no_permission")}
        text={t("no_permission_description")}
      />
    );
  }

  const list =
    loading || scope === null ? (
    <Group justify="center" py="xl">
      <Loader size="sm" />
    </Group>
  ) : (
    <Stack gap="md">
      <Title order={5}>{t("assignment_request_plural")}</Title>
      <SegmentedControl
        value={scope}
        onChange={setScope}
        data={[
          ...(canRequest ? [{ label: t("mine"), value: "mine" }] : []),
          { label: t("waiting_org"), value: "waiting_org" },
          { label: t("waiting_platform"), value: "waiting_platform" },
          { label: t("approved"), value: "approved" },
          { label: t("declined"), value: "declined" },
          { label: t("withdrawn"), value: "withdrawn" },
        ]}
      />
      <Stack gap="xs">
        {!requests.length && <EmptyMessage />}
        {requests.map((request) => {
          const waiting =
            request.workflow_status === 0 || request.workflow_status === 1;
          const usePlatform = request.workflow_status === 1;
          const showDecide =
            waiting &&
            ((usePlatform && canPlatformDecide) ||
              (!usePlatform && canOrgDecide));
          const showWithdraw = canWithdraw(request);

          return (
            <Card key={request.id}>
              <Flex gap="sm" align="flex-start">
                <div style={{ flexGrow: 1 }}>
                  <Group gap="xs" mb={4}>
                    <Text fw={700}>
                      {personLabel(request.user, request.user_id)}
                    </Text>
                    <Badge size="sm" variant="light">
                      {request.workflow_status_formatted ||
                        request.status_formatted}
                    </Badge>
                  </Group>
                  <Text size="sm" c="dimmed">
                    {t("assessment_instrument_singular")}:{" "}
                    {instrumentLabel(
                      request.assessment_instrument,
                      request.assessment_instrument_id
                    )}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {t("requested_by")}:{" "}
                    {personLabel(request.requested_by, request.requested_by_id)}
                  </Text>
                  {(waiting || request.assessment_id) && (
                    <AssessmentViewerLinksPanel
                      organizationId={organizationId}
                      assessmentAssignmentRequestId={
                        waiting ? request.id : null
                      }
                      assessmentId={
                        !waiting && request.assessment_id
                          ? request.assessment_id
                          : null
                      }
                      assigneeUserId={request.user_id}
                      allowMutate={waiting}
                    />
                  )}
                </div>
                {(showDecide || showWithdraw) && (
                  <Flex gap="xs" direction="column">
                    {showDecide && (
                      <Flex gap="xs">
                        <Button
                          size="xs"
                          color="green"
                          loading={busyId === request.id}
                          onClick={() => decide(request.id, true, usePlatform)}
                        >
                          {t("approve")}
                        </Button>
                        <Button
                          size="xs"
                          color="red"
                          variant="light"
                          loading={busyId === request.id}
                          onClick={() => decide(request.id, false, usePlatform)}
                        >
                          {t("decline")}
                        </Button>
                      </Flex>
                    )}
                    {showWithdraw && (
                      <Button
                        size="xs"
                        variant="default"
                        loading={busyId === request.id}
                        onClick={() => withdraw(request.id)}
                      >
                        {t("withdraw")}
                      </Button>
                    )}
                  </Flex>
                )}
              </Flex>
            </Card>
          );
        })}
      </Stack>
    </Stack>
  );

  return (
    <ManagementLayout
      dataComponent={list}
      createComponent={
        <OrganizationAssignmentRequestCreate
          organizationId={organizationId}
          onSuccess={fetchData}
        />
      }
    />
  );
}
